import React, { useEffect, useState } from 'react';
import { makeStyles, styled } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import jwtDecode from 'jwt-decode';
import { customLogin, getAuthProviders, getLoginUrl } from '../../services/api';
import { useLocalStorage } from '../../services/hooks/useLocalStorage';
import { usePromiseTracker } from 'react-promise-tracker';

import Loading from '../Loading';

import EmptyTargetBig from '../../img/empty-target.png';
import KeyHand from '../../img/key-hand.png';
import STCLogo from '../../img/small-body-logo-stc.png';
import STCLogoBig from '../../img/body-logo-stc.png';
import StratusLogo from '../../img/small-body-logo-stratus.png';
import StratusLogoBig from '../../img/body-logo-stratus.png';
import Swoosh from '../../img/purple-swoosh.png';
import SwooshBig from '../../img/purple-swoosh-big.png';
import Caution from '../../img/not-found.png';
import CautionSmall from '../../img/not-found-small.png';
import { Button, TextField } from '@mui/material';

const InputTextField = styled(TextField)(() => ({
    '& .MuiOutlinedInput-root': {
        overflow: 'hidden',
        border: '1px solid #707070',
        backgroundColor: 'white',
        height: '31px',
        width: '405px',
        '@media (min-width: 1000px)': {
            width: '450px',
        }
    },
}))

const useStyles = makeStyles({
    button_provider: {
        width: '270px',
        height: '36px',
    },
    button_provider_span: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    buttons: {
        overflow: 'auto',
        maxHeight: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
    },
    button_container: {
        width: '70%',
        '@media (max-width: 800px)': {
            width: '90%',
        },
        '@media (min-width: 1000px)': {
            width: '450px',
        }
    },
    caution: {
        marginBottom: '60px',
        marginRight: '50px',
        '@media (max-width: 800px)': {
            marginBottom: '57px',
            marginRight: '31px',
        },
    },
    container: {
        display: 'grid',
        gridTemplateRows: '49px 486px',
        gridTemplateColumns: '502px auto',
        gridTemplateAreas: '"header header" "card content"',
        overflow: 'hidden',
        '@media (max-width: 800px)': {
            gridTemplateColumns: '448px auto',
        },
        '@media (min-width: 1000px)': {
            gridTemplateColumns: '642px auto',
        },
    },
    container_error: {
        display: 'grid',
        gridTemplateRows: '49px 486px',
        gridTemplateColumns: '396px auto',
        gridTemplateAreas: '"header header" "card content"',
        overflow: 'hidden',
        '@media (max-width: 800px)': {
            gridTemplateColumns: '238px auto',
        },
    },
    input_label: {
        color: 'white',
        fontSize: '19px',
        fontFamily: 'sans-serif',
        width: '70%',
        '@media (max-width: 800px)': {
            width: '90%',
        },
        '@media (min-width: 1000px)': {
            width: '450px',
        },
    },
    label: {
        color: 'white',
        fontSize: '21px',
        fontFamily: 'sans-serif',
        width: '70%',
        '@media (max-width: 800px)': {
            width: '90%',
        },
        '@media (min-width: 1000px)': {
            width: '450px',
        },
    },
    label_error: {
        color: 'white',
        fontSize: '18px',
        fontFamily: 'sans-serif'
    },
    left: {
        display: 'grid',
        backgroundColor: '#5F3FA5',
        minHeight: '486px',
        paddingTop: '60px',
        justifyItems: 'center',
        gridTemplateRows: '140px 40px 24px 44px 24px 44px',
        '@media (max-width: 800px)': {
          paddingTop: '40px',
        }
    },
    left_logo_error: {
        display: 'flex',
        height: 'calc(100vh - 66px)',
        alignItems: 'center',
    },
    left_no_auth: {
        display: 'grid',
        backgroundColor: '#5F3FA5',
        minHeight: '546px',
        justifyItems: 'center',
        alignItems: 'center',
        gridTemplateRows: '420px',
    },
    logo: {
        zIndex: '1',
        marginRight: '30px',
        maxWidth: '450px',
        '@media (max-width: 800px)': {
            marginRight: '0',
            maxWidth: '378px',
        },
    },
    no_providers_image: {
        transform: 'scaleX(-1)',
        zIndex: '-1',
    },
    provider_container: {
        position: 'absolute',
        right: '40px',
        minHeight: '486px',
        display: 'grid',
        justifyItems: 'center',
        gridTemplateRows: '130px',
        '@media (max-width: 800px)': {
            right: '4px',
        },
    },
    provider_select: {
        color: '#603FA5',
        display: 'flex',
        justifyItems: 'center',
        alignItems: 'flex-end',
        marginBottom: '20px',
    },
    provider_select_hidden: {
        visibility: 'hidden',
    },
    right: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 56px)',
    },
    right_action: {
        display: 'grid',
        backgroundColor: '#5F3FA5',
        paddingRight: '35px',
        paddingTop: '60px',
        gridTemplateRows: '200px 80px 32px',
        minHeight: '486px',
        overflow: 'hidden',
        color: 'white',
        justifyItems: 'center'
    },
    swoosh: {
        backgroundImage: `url(${SwooshBig})`,
        height: "535px",
        width: "74px",
        position: 'absolute',
        top: '1px',
        left: '500px',
        transform: 'scaleX(-1)',
        '@media (max-width: 800px)': {
            backgroundImage: `url(${Swoosh})`,
            height: "486px",
            width: "78px",
            top: '-7px',
            left: '446px',
        },
        '@media (min-width: 1000px)': {
            left: '640px',
        },
    },
    swoosh_left: {
        backgroundImage: `url(${Swoosh})`,
        height: "486px",
        width: "78px",
        position: 'absolute',
        top: '-7px',
        left: '320px',
        '@media (max-width: 800px)': {
            left: '162px',
        },
    },
    swoosh_left_big: {
        backgroundImage: `url(${SwooshBig})`,
        height: "535px",
        width: "74px",
        position: 'absolute',
        top: '1px',
        left: '324px'
    },
    target: {
        position: 'absolute',
        zIndex: '-1',
        right: '0',
        transform: 'scaleX(-1)',
        '@media (max-width: 800px)': {
            width: '282px',
            height: '322px',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
        },
    },
    target_left: {
        backgroundImage: `url(${EmptyTargetBig})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        zIndex: '-1',
        width: '414px',
        height: '450px',
        '@media (max-width: 800px)': {
            width: '282px',
            height: '322px',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
        },
    },
})

const Cognito = (props) => {
    const [stratus] = useLocalStorage("stratus", false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [cognitoProvider, setCognitoProvider] = useState(null);
    const [authProviders, setAuthProviders] = useState([]);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const classes = useStyles();
    const ns = stratus ? 'stratus' : 'scantrip';
    const { t } = useTranslation(ns);
    const { promiseInProgress } = usePromiseTracker({ delay: 150 })
    const tenant = props.tenant;

    useEffect(() => {
        const runGetAuthProviders = async () => {
            try {
                const providers = await getAuthProviders(tenant)
                const providerList = []
                let cognito = null
                for (const provider of providers) {
                    if (!provider.enabled) {
                        continue
                    }
                    if (provider.type === "native") {
                        cognito = provider
                        continue
                    }
                    providerList.push(provider)
                }
                if (providerList.length === 1 && cognito === null) {
                    selectProvider(providerList[0])
                    return
                }
                if (cognito !== null) {
                    setCognitoProvider(cognito)
                }
                setAuthProviders(providerList.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                setLoading(false)
            } catch (e) {
                console.error(e)
                setLoading(false)
            }
        }
        setLoading(true)
        runGetAuthProviders()
    // eslint-disable-next-line
    }, [tenant]);

    const selectProvider = async (provider) => {
        try {
            setLoading(true)
            const loginUrl = await getLoginUrl(props.tenant, provider.id, props.lastScannedCardId)
            window.location = loginUrl
        } catch (e) {
            console.error(e)
            setLoading(false)
        }
    }

    const nativeLogin = async () => {
        const resp = await customLogin(username, password, props.tenant, props.lastScannedCardId)
        if (!resp.ok) {
            console.log(resp)
            setError(t('cognito:credentials_invalid'))
            return
        }
        const idToken = resp.body.idToken;
        const accessToken = resp.body.accessToken;
        const refreshToken = resp.body.refreshToken;
        const decodedIdToken = jwtDecode(idToken);
        const decodedAccessToken = jwtDecode(accessToken);
        const user = decodedIdToken.email || decodedAccessToken.username
        props.setters.setTenant({ id: tenant.id, name: tenant.name, plan: tenant.plan, region: tenant.region, slug: tenant.slug });
        props.setters.setIdToken(idToken);
        props.setters.setAccessToken(accessToken);
        props.setters.setRefreshToken(refreshToken);
        props.setters.setClient(tenant.client);
        props.setters.setUser(user);
    }

    const logo = window.innerWidth > 800 ? (stratus ? StratusLogoBig : STCLogoBig) : (stratus ? StratusLogo : STCLogo);
    const caution = window.innerWidth > 800 ? Caution : CautionSmall

    const generateLeftSide = () => {
        if (loading) {
            return <div id="left" className={classes.left_no_auth}></div>
        }
        const cognitoPresent = cognitoProvider !== null;
        if (!cognitoPresent) {
            return (
                <div id="left" className={classes.left_no_auth}>
                    <img src={logo} alt="Dispatcher ScanTrip Cloud" className={classes.logo} />
                </div>
            )
        }
        return (
            <div id="left" className={classes.left}>
                <img src={logo} alt="Dispatcher ScanTrip Cloud" className={classes.logo} />
                <div className={classes.label}>
                    {t('cognito:login')}
                </div>
                <div className={classes.input_label}>{t('cognito:username_or_email')}</div>
                <InputTextField
                    id="auth-username"
                    variant="outlined"
                    value={username}
                    onInput={(e) => setUsername(e.target.value)}
                />
                <div className={classes.input_label}>{t('Password')}</div>
                <InputTextField
                    id="auth-password"
                    variant="outlined"
                    type="password"
                    value={password}
                    onInput={(e) => setPassword(e.target.value)}
                />
                <div className={classes.button_container}>
                    <Button
                        variant="contained"
                        onClick={() => nativeLogin()}
                        disabled={!username || !password}
                        sx={{
                            color: 'white !important',
                            backgroundColor: '#272261',
                            width: '82px',
                            height: '36px',
                            float: 'right',
                            '&:active': {
                                backgroundColor: '#272261',
                            },
                            '&:focus': {
                                backgroundColor: '#272261',
                            },
                            '&:hover': {
                                backgroundColor: '#272261',
                            },
                        }}
                    >
                        {t('Login')}
                    </Button>
                </div>
            </div>
        )
    }

    const generateRightSide = () => {
        if (loading) {
            return <div id="right" className={classes.right}>
                <div id="swoosh" className={classes.swoosh}></div>
                <img className={classes.target} src={EmptyTargetBig} alt="Target" />
            </div>
        }
        let provider_select_class = classes.provider_select
        let providersList;
        if (authProviders.length === 0) {
            providersList = (
                <img className={classes.no_providers_image} src={KeyHand} alt="Hand holding a key" />
            )
            provider_select_class += ' ' + classes.provider_select_hidden
        } else {
            providersList = authProviders.map(provider => {
                return (
                    <Button
                        key={provider.id}
                        variant="contained"
                        onClick={() => selectProvider(provider)}
                        className={classes.button_provider}
                        sx={{
                            backgroundColor: '#8D74C3',
                            marginTop: '8px',
                            textTransform: 'none',
                            '&:active': {
                                backgroundColor: '#272261',
                            },
                            '&:focus': {
                                backgroundColor: '#272261',
                            },
                            '&:hover': {
                                backgroundColor: '#272261',
                            },
                        }}
                    >
                        <span className={classes.button_provider_span}>{provider.name}</span>
                    </Button>
                )
            })
        }
        return (
            <div id="right" className={classes.right}>
                <div id="swoosh" className={classes.swoosh}></div>
                <img className={classes.target} src={EmptyTargetBig} alt="Target" />
                <div className={classes.provider_container}>
                    <div className={provider_select_class}>
                        <b style={{ margin: '0 4px' }}>{t('cognito:or')}</b> {t('cognito:provider')}
                    </div>
                    <div className={classes.buttons}>
                        {providersList}
                    </div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className={classes.container_error}>
                <div id="left-logo" className={classes.left_logo_error}>
                    <div className={classes.target_left}>
                        <img src={caution} className={classes.caution} alt="Caution" />
                    </div>
                    <div id="swoosh" className={window.innerWidth > 1000 ? classes.swoosh_left_big : classes.swoosh_left}></div>
                </div>
                <div id="right-action" className={classes.right_action}>
                    <img src={logo} alt="Dispatcher ScanTrip Cloud" className={classes.logo} />
                    <div id="error-text" className={classes.label_error}>{error}</div>
                    <Button
                        variant="contained"
                        onClick={() => setError('')}
                        sx={{
                            backgroundColor: '#272261',
                            width: '100px',
                            height: '36px',
                            float: 'right',
                            '&:active': {
                                backgroundColor: '#272261',
                            },
                            '&:focus': {
                                backgroundColor: '#272261',
                            },
                            '&:hover': {
                                backgroundColor: '#272261',
                            },
                        }}
                    >
                        {t('Continue')}
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className={classes.container}>
            {props.loading || loading || promiseInProgress ? <Loading /> : <></>}
            {generateLeftSide()}
            {generateRightSide()}
        </div>
    )
}

export default Cognito